import gql from 'graphql-tag';

import { Payline } from '@aps-management/primapp-common';

/**
 * Capture en un clic sur carte enregistrée, portant l'intention de livraison et
 * rapportant le verdict de la livraison faite par le serveur.
 *
 * POURQUOI CE DOUBLON DU PAQUET COMMUN
 *
 * `Payline.api.captureSubscriber` est parfait, à deux champs près : son document
 * GraphQL ne déclare ni la variable `deliveryIntent`, ni le champ `delivery` de
 * la réponse. Or ni l'une ni l'autre n'existent pour le serveur s'ils ne sont pas
 * déclarés : passer l'intention dans `variables` ne ferait rien du tout, et le
 * verdict reviendrait toujours indéfini - silencieusement, dans les deux cas.
 *
 * Le motif du doublon est le RAYON D'IMPACT, pas une contrainte de publication :
 * `primapp-common` est ici monté en lien (`node_modules/@aps-management/primapp-common`
 * est un symlink vers `~/.config/yarn/link/`), donc une modification y serait
 * prise sans republication - mais elle changerait d'un coup le document de TOUS
 * les écrans de TOUS les fronts qui montent ce lien. Le document est donc déclaré
 * ici, exactement comme il l'est déjà dans l'écran de paiement compétition, et
 * pour la même raison.
 *
 * Conséquence de ce lien, à contrôler avant chaque build : les deux fronts web
 * compilent l'ARBRE DE TRAVAIL local de `primapp-common`, pas une version
 * publiée. Un fichier modifié et non commité là-bas part en production ici.
 *
 * CE QUE ÇA RÉPARE
 *
 * Le paiement en un clic n'ouvre aucune page Monext : il encaisse dans
 * l'aller-retour de la mutation, sans jeton et sans notification. Le CRÉDIT, lui,
 * appartenait à un SECOND appel émis par le navigateur. Tout ce qui coupait entre
 * les deux - onglet fermé, réseau perdu, attente qui fait renoncer - prenait
 * l'argent sans rien livrer. Cas mesuré : Antoine GARREAU, La Rochelle Sud,
 * 25/07/2026, deux débits de 3,50 EUR pour un seul seau crédité.
 *
 * Avec l'intention, le serveur livre lui-même dans la foulée de la capture et le
 * dit dans `delivery`. Sans elle, rien ne change.
 *
 * CE QU'IL NE COUVRE PAS
 *
 * La reprise après challenge 3DS (`captureSubscriberAfter3DS`) reste sur le
 * document commun, sans intention : un paiement en un clic challengé par la
 * banque garde donc le comportement d'avant, le navigateur livre. Ce n'est pas
 * une régression - c'est exactement l'état actuel - mais c'est un trou qui reste
 * ouvert, à traiter dans son propre lot.
 *
 * FIDÉLITÉ AU COMPORTEMENT D'ORIGINE
 *
 * La sélection de champs, le traitement du challenge 3DS (`needs3DS` est rendu
 * tel quel, ce n'est pas une erreur) et la conversion des erreurs sont repris de
 * `primapp-common` à l'identique. Seuls la variable, l'argument et le champ
 * `delivery` sont ajoutés. Ne pas s'en écarter : l'appelant traite déjà ces cas.
 */
const CAPTURE_SUBSCRIBER_WITH_INTENT = gql`
  mutation PaylineCaptureSubscriberWithIntent(
    $amount: Int!
    $reference: String!
    $subscriber: PaylineSubscriber!
    $deliveryIntent: String
  ) {
    paylineCaptureSubscriber(
      amount: $amount
      reference: $reference
      subscriber: $subscriber
      deliveryIntent: $deliveryIntent
    ) {
      transactionId
      token
      authorizationNumber
      resultCode
      needs3DS
      # Ce que le serveur a livre tout seul dans la foulee de la capture.
      #
      # Demande ici plutot que dans un document separe parce que c'est la MEME
      # reponse : le verdict doit arriver avec l'encaissement, sinon on remplace
      # une seconde requete par une autre et le trou reste ouvert.
      #
      # Ne rien lire d'autre que "done". "status" et "scope" sont du diagnostic,
      # leur liste appartient au serveur et s'allongera. Un ecran qui n'attend
      # pas ce champ le recoit nul et se comporte comme avant.
      delivery {
        done
        status
        scope
      }
      threeDSData {
        actionUrl
        actionMethod
        pareq
        pareqFieldName
        pareqFieldValue
        md
        mdFieldName
        mdFieldValue
        termUrl
        mpiResult
        transientData
      }
    }
  }
`;

/**
 * Signature identique à `Payline.api.captureSubscriber`, avec `deliveryIntent`
 * en plus dans `variables`. Une intention absente ou nulle redonne exactement le
 * comportement d'avant : le serveur n'écrit alors aucune session et ne livre
 * rien de lui-même.
 *
 * @param {object} client    Client Apollo visant master
 * @param {object} variables amount, reference, subscriber, deliveryIntent
 */
const captureSubscriberWithIntent = (client, variables) =>
  client
    .mutate({ mutation: CAPTURE_SUBSCRIBER_WITH_INTENT, variables })
    .then(({ data }) => {
      const response = data ? data.paylineCaptureSubscriber : undefined;
      // Challenge 3DS demandé : aucun débit n'a eu lieu, ce n'est pas une erreur.
      if (response && response.needs3DS) return response;
      if (!Payline.errors.isPaylineSuccess(response)) {
        throw Payline.errors.handlePaylineError(null, response);
      }
      return response;
    })
    .catch((error) => {
      throw Payline.errors.handlePaylineError(error);
    });

export default captureSubscriberWithIntent;
export { CAPTURE_SUBSCRIBER_WITH_INTENT };
